import React from 'react';
import {
    Box,
    CircularProgress,
    Fade,
    IconButton,
    Modal,
    Typography
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';

//
// Full-screen capture viewer. One overlay serves both gate captures and the
// "View ID" driver-license scan — the caller passes a ready image (captures)
// or a loading/`imageSrc` pair it resolves on open (the signed DL url).
//
const Lightbox = ({ open, onClose, title, meta, imageSrc, loading, error }) => (
    <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        slotProps={{
            backdrop: {
                sx: {
                    backgroundColor: 'rgba(1, 45, 39, 0.72)',
                    backdropFilter: 'blur(12px)'
                }
            }
        }}
    >
        <Fade in={open}>
            <Box
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 3, md: 6 },
                    outline: 'none'
                }}
            >
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        position: 'absolute',
                        top: 24,
                        right: 24,
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.16)',
                        '&:hover': {
                            color: 'success.light',
                            borderColor: 'success.light'
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>

                <Box
                    component="figure"
                    onClick={event => event.stopPropagation()}
                    sx={{ m: 0, maxWidth: 'min(1040px, 100%)', width: 'auto' }}
                >
                    <Box
                        sx={{
                            minWidth: { xs: 260, sm: 420 },
                            minHeight: 240,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: loading || error ? 'rgba(0,0,0,0.35)' : 'transparent'
                        }}
                    >
                        {loading && <CircularProgress sx={{ color: '#fff' }} />}

                        {!loading && error && (
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.72)',
                                    fontSize: '.9rem',
                                    p: 4
                                }}
                            >
                                This capture isn&apos;t available.
                            </Typography>
                        )}

                        {!loading && !error && imageSrc && (
                            <Box
                                component="img"
                                src={imageSrc}
                                alt={title || 'Capture'}
                                sx={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    maxHeight: '72vh',
                                    borderRadius: 2,
                                    boxShadow: 8
                                }}
                            />
                        )}
                    </Box>

                    {(title || meta) && (
                        <Box
                            component="figcaption"
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                                pt: 1.75,
                                px: 0.5
                            }}
                        >
                            <Typography
                                sx={{ color: '#fff', fontSize: '.85rem', fontWeight: 600 }}
                            >
                                {title}
                            </Typography>
                            {meta && (
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.6)',
                                        fontSize: '.75rem',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {meta}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Fade>
    </Modal>
);

export default Lightbox;
